import React from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Alert, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { humanizeEnum } from '../../utils/format';

/** Codes whose plain-casing humanisation reads wrong. */
const CODE_LABELS: Record<string, string> = {
    NO_PAYROLL_ROWS: 'No payroll rows',
    INCLUDES_DRAFT: 'Includes draft payroll',
    ORPHAN_BLOCK: 'orphan-punch blocking',
    BELOW_GRADE_FLOOR: 'Below grade floor',
    BELOW_MINIMUM_WAGE: 'Below minimum wage',
    NO_GRADE_ASSIGNED: 'No grade assigned',
    INACTIVE_GRADE: 'Inactive grade',
    UNVERIFIED: 'Unverified',
    SEEDED: 'Gazette',
    EDITED: 'Edited',
};

/** `INCLUDES_DRAFT` -> "Includes draft payroll" (falls back to sentence-casing the code). */
export const humanizeCode = (code: string | null | undefined): string =>
    code ? (/^[A-Z0-9_]+$/.test(code) ? humanizeEnum(code, CODE_LABELS) : code) : '—';

const groupThousands = (n: string): string => n.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

/**
 * Turns one server-sent register note into customer-readable text: drops "(camelCaseField)"
 * parentheticals, replaces SCREAMING_SNAKE enum codes with words, and formats 4-decimal raw
 * amounts ("260000.0000") as money ("260,000.00"). No information is removed.
 */
export const humanizeNote = (note: string): string =>
    note
        .replace(/\s*\(\s*[a-z]+(?:[A-Z][a-z0-9]*)+\s*\)/g, '')
        .replace(/\b[A-Z]{2,}(?:_[A-Z]{2,})+\b/g, m => {
            const label = humanizeCode(m);
            return CODE_LABELS[m] ? label : label.toLowerCase();
        })
        .replace(/(\d+)\.(\d{4})\b/g, (m, i: string) => {
            // Money-sized values are rounded to 2 decimals; small rates/ratios keep all 4.
            if (Number(i) < 1000) return m;
            const [whole, cents] = Number(m).toFixed(2).split('.');
            return `${groupThousands(whole)}.${cents}`;
        });

interface Props {
    notes: string[];
    testId: string;
    /** Optional mapper applied before humanising (e.g. a register's existing friendlyNote). */
    map?: (note: string) => string;
}

/**
 * One short summary line plus a collapsed accordion holding the full list of notes — the
 * default view stays clean while every statutory disclosure stays one click away.
 */
const RegisterNotes: React.FC<Props> = ({ notes, testId, map }) => {
    if (notes.length === 0) return null;
    const text = notes.map(n => humanizeNote(map ? map(n) : n));
    return (
        <Accordion sx={{ mb: 2 }} disableGutters data-testid={testId} data-help="statutory.notes">
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Alert severity="info" sx={{ p: 0, bgcolor: 'transparent' }} data-testid={`${testId}-summary`}>
                    {text.length === 1 ? '1 note about how these figures were computed' : `${text.length} notes about how these figures were computed`}
                </Alert>
            </AccordionSummary>
            <AccordionDetails>
                {text.map((t, i) => (
                    <Typography key={i} variant="body2" sx={{ mb: 1 }}>{t}</Typography>
                ))}
            </AccordionDetails>
        </Accordion>
    );
};

export default RegisterNotes;
